import { Injectable } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { Observable, map, tap } from "rxjs";
import { ApiService } from "../api.service";
import { IdempotencyKeyProvider } from "../idempotency-key.provider";
import { FrontendLoggerService } from "../frontend-logger.service";
import { HomeDecisionActionApiExecutionService } from "./home-decision-action-api-execution.service";
import { HomeApiEtagClientService } from "./home-api-etag-client.service";
import { HOME_API_ROUTES } from "./home-api-routes";
import {
  HomeDecisionDetailResponse,
  HomeDecisionPageResponse,
  HomeIaDecisionActionRequest,
  HomeIaDecisionActionResponse,
  HomeNextBestDecisionResponse,
  DatesSummaryResponse,
  HomeActivityEventResponse,
  HomeActivityResponse,
  HomeMetricsResponse,
  HomePulseResponse,
  ReclassifyDocumentResponse,
} from "../../models/home/dtos";

export interface DatesSummaryFilters {
  roles?: string[];
}

export interface DecisionFilters {
  state?: string;
  assignedToUserId?: string;
  q?: string;
  from?: string;
  to?: string;
}

@Injectable({ providedIn: "root" })
export class HomeApiService {
  private readonly DEFAULT_PAGE = 1;
  private readonly DEFAULT_DECISION_PAGE_SIZE = 20;
  private readonly DEFAULT_ACTIVITY_PAGE_SIZE = 6;

  constructor(
    private readonly api: ApiService,
    private readonly idempotencyKeyProvider: IdempotencyKeyProvider,
    private readonly etagClient: HomeApiEtagClientService,
    private readonly decisionActionExecution: HomeDecisionActionApiExecutionService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  getPulse(ifNoneMatch?: string): Observable<HomePulseResponse> {
    return this.getWithEtag<HomePulseResponse>(HOME_API_ROUTES.PULSE, undefined, ifNoneMatch);
  }

  getDatesSummary(filters?: DatesSummaryFilters): Observable<DatesSummaryResponse> {
    const params: Record<string, string | string[]> = {};
    if (filters?.roles?.length) {
      params["roles"] = filters.roles;
    }
    return this.getWithEtag<DatesSummaryResponse>(HOME_API_ROUTES.DATES_SUMMARY, params);
  }

  getDecisions(
    page: number = this.DEFAULT_PAGE,
    pageSize: number = this.DEFAULT_DECISION_PAGE_SIZE,
    filters?: DecisionFilters
  ): Observable<HomeDecisionPageResponse> {
    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize)
    };

    if (filters?.state) {
      params["state"] = filters.state;
    }
    if (filters?.assignedToUserId) {
      params["assignedToUserId"] = filters.assignedToUserId;
    }
    if (filters?.q) {
      params["q"] = filters.q;
    }
    if (filters?.from) {
      params["from"] = filters.from;
    }
    if (filters?.to) {
      params["to"] = filters.to;
    }

    return this.getWithEtag<HomeDecisionPageResponse>(HOME_API_ROUTES.DECISIONS, params);
  }

  getNextDecision(filters?: DecisionFilters, ifNoneMatch?: string): Observable<HomeNextBestDecisionResponse> {
    const params: Record<string, string> = {};
    if (filters?.state) {
      params["state"] = filters.state;
    }
    if (filters?.assignedToUserId) {
      params["assignedToUserId"] = filters.assignedToUserId;
    }

    return this.getWithEtag<HomeNextBestDecisionResponse>(`${HOME_API_ROUTES.DECISIONS}/next`, params, ifNoneMatch);
  }

  getDecisionDetail(decisionId: string, ifNoneMatch?: string): Observable<HomeDecisionDetailResponse> {
    return this.getWithEtag<HomeDecisionDetailResponse>(`${HOME_API_ROUTES.DECISIONS}/${decisionId}`, undefined, ifNoneMatch);
  }

  executeDecisionAction(
    decisionId: string,
    request: HomeIaDecisionActionRequest,
    idempotencyKey?: string
  ): Observable<HomeIaDecisionActionResponse> {
    const headers = this.buildIdempotencyHeaders(idempotencyKey);
    const execution = this.decisionActionExecution.resolve(HOME_API_ROUTES.DECISIONS, decisionId, request);
    this.frontendLogger.info(
      `[HomeApiService] executeDecisionAction request: ${JSON.stringify({ decisionId, endpoint: execution.url, request })}`
    );

    const request$ = execution.kind === "reevaluate"
      ? this.api
          .post<ReclassifyDocumentResponse>(execution.url, execution.body, headers)
          .pipe(map((response) => this.decisionActionExecution.toReevaluationActionResponse(decisionId, request, response)))
      : this.api.post<HomeIaDecisionActionResponse>(execution.url, execution.body, headers);

    return request$
      .pipe(
        tap({
          next: () =>
            this.frontendLogger.info(`[HomeApiService] executeDecisionAction success: decisionId=${decisionId}`),
          error: (error) =>
            this.frontendLogger.error(
              `[HomeApiService] executeDecisionAction failed: ${JSON.stringify({ decisionId, error })}`
            )
        }),
        tap(() => this.invalidateDecisionCaches())
      );
  }

  getMetrics(): Observable<HomeMetricsResponse> {
    return this.getWithEtag<HomeMetricsResponse>(HOME_API_ROUTES.METRICS);
  }

  getLastActivity(
    page: number = this.DEFAULT_PAGE,
    pageSize: number = 1
  ): Observable<HomeActivityEventResponse> {
    const params = { page: String(page), pageSize: String(pageSize) };
    return this.getWithEtag<HomeActivityEventResponse>(HOME_API_ROUTES.LAST_ACTIVITY, params);
  }

  getActivity(
    page: number = this.DEFAULT_PAGE,
    pageSize: number = this.DEFAULT_ACTIVITY_PAGE_SIZE
  ): Observable<HomeActivityResponse> {
    const params = { page: String(page), pageSize: String(pageSize) };
    return this.getWithEtag<HomeActivityResponse>(HOME_API_ROUTES.ACTIVITY, params);
  }

  getActivityHistory(page: number, pageSize: number): Observable<HomeActivityResponse> {
    const params = {
      page: String(page),
      pageSize: String(pageSize)
    };
    return this.getWithEtag<HomeActivityResponse>(HOME_API_ROUTES.ACTIVITY_HISTORY, params);
  }

  private getWithEtag<T>(endpoint: string, params?: Record<string, any>, ifNoneMatch?: string): Observable<T> {
    return this.etagClient.getWithEtag<T>(endpoint, params, ifNoneMatch);
  }

  private invalidateDecisionCaches(): void {
    this.etagClient.clearByPrefix(HOME_API_ROUTES.DECISIONS);
  }

  private buildIdempotencyHeaders(idempotencyKey?: string): HttpHeaders {
    const key = idempotencyKey ?? this.idempotencyKeyProvider.generate();
    return new HttpHeaders().set("Idempotency-Key", key);
  }
}
